import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';

import { StartCommonGame } from 'components/commonGame/StartCommonGame';

import { CommonPacks } from './CommonPacks';
import type {
  CommonPacksAvailableOnboardingLeaderboardsQuery,
  CommonPacksAvailableOnboardingLeaderboardsQueryVariables,
  CommonPacksQuery,
  CommonPacksQueryVariables,
} from './__generated__/index.graphql';

const COMMON_PACKS_QUERY = gql`
  query CommonPacksQuery($sport: Sport!) {
    market {
      id
      ...CommonPacks_marketRoot
    }
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        commonGameOnboarded
      }
    }
  }
  ${CommonPacks.fragments.marketRoot}
` as TypedDocumentNode<CommonPacksQuery, CommonPacksQueryVariables>;

const COMMON_PACK_AVAILABLE_ONBOARDING_LEADERBOARDS_QUERY = gql`
  query CommonPacksAvailableOnboardingLeaderboardsQuery($sport: Sport!) {
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        availableOnboardingLeaderboards(rarity: common)
      }
    }
  }
` as TypedDocumentNode<
  CommonPacksAvailableOnboardingLeaderboardsQuery,
  CommonPacksAvailableOnboardingLeaderboardsQueryVariables
>;

export const loader = queryLoader(COMMON_PACKS_QUERY, ({ sport }) => ({
  sport,
}));

const CheckAvailableOnboardingLeaderboards = ({
  availableElement,
  notAvailableElement,
}: {
  availableElement: ReactNode;
  notAvailableElement: ReactNode;
}): ReactNode => {
  const { sport } = useSportContext();
  const { data, loading } = useQuery(
    COMMON_PACK_AVAILABLE_ONBOARDING_LEADERBOARDS_QUERY,
    {
      variables: {
        sport,
      },
    }
  );

  const { availableOnboardingLeaderboards } =
    data?.currentUser?.anyUserSportProfile || {};

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  if (availableOnboardingLeaderboards) {
    return availableElement;
  }

  return notAvailableElement;
};
export const CommonPacksPage = () => {
  const { data, refetch } = useQueryLoader<CommonPacksQuery>();
  const { market, currentUser } = data;

  if (currentUser?.anyUserSportProfile?.commonGameOnboarded) {
    return <CommonPacks marketRoot={market} refetch={refetch} />;
  }

  // fetch availableOnboardingLeaderboards only if user is not onboarded as it's expensive
  return (
    <RequireAuth>
      <CheckAvailableOnboardingLeaderboards
        availableElement={
          <Vertical
            center
            style={{
              height: 'calc(100vh - var(--current-stack-height))',
            }}
          >
            <StartCommonGame />
          </Vertical>
        }
        notAvailableElement={
          <CommonPacks marketRoot={market} refetch={refetch} />
        }
      />
    </RequireAuth>
  );
};
